import { LocationFeatureResponse } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { SalesforceAccountProductBundle } from '@weave/schema-gen-ts/dist/schemas/salesforce/v1/salesforce.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { FreeTrialSubscriptionTypes } from '@frontend/api-free-trial-subscription';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { i18next } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { SelfUpgradeActionType } from './constants/self-upgrade';
import { LocationSubscriptionData, LocationSubscriptionWithBillingData } from './types';

export const getActionText = (
  actionType: SelfUpgradeActionType
): { primaryButtonText?: string; actionInfoText?: string } => {
  switch (actionType) {
    case SelfUpgradeActionType.REQUEST_UPGRADE:
      return {
        primaryButtonText: i18next.t('Request Upgrade', { ns: 'featurePromotion>>self-upgrade-actions' }),
        actionInfoText: i18next.t('A request will be sent to your admin', {
          ns: 'featurePromotion>>self-upgrade-actions',
        }),
      };
    case SelfUpgradeActionType.SCHEDULE_CALL:
      return {
        primaryButtonText: i18next.t('Call to Upgrade', { ns: 'featurePromotion>>self-upgrade-actions' }),
      };
    case SelfUpgradeActionType.FREE_TRIAL:
      return {
        primaryButtonText: i18next.t('Try for Free', { ns: 'featurePromotion>>self-upgrade-actions' }),
      };
    case SelfUpgradeActionType.UPGRADE_NOW:
      return {
        primaryButtonText: i18next.t('Upgrade Now', { ns: 'featurePromotion>>self-upgrade-actions' }),
      };
    default:
      return {
        primaryButtonText: '',
        actionInfoText: '',
      };
  }
};

export const getActionIconName = (actionType: SelfUpgradeActionType): IconName => {
  // switch case added for future use
  switch (actionType) {
    default:
      return 'award';
  }
};

export const getNotificationDismissActionText = (actionType: PlatformLocationFeatureV1.Types.Action): string => {
  switch (actionType) {
    case PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN:
      return i18next.t('Never show again', { ns: 'featurePromotion>>self-upgrade-actions' });
    case PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS:
      return i18next.t('Remind me in 3 days', { ns: 'featurePromotion>>self-upgrade-actions' });
    case PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_IN_NEXT_QUARTER:
      return i18next.t('Remind me in next quarter', { ns: 'featurePromotion>>self-upgrade-actions' });
    default:
      return i18next.t('Dismiss', { ns: 'featurePromotion>>self-upgrade-actions' });
  }
};

export const buildLocationSubscriptionData = ({
  selectedLocationIds,
  locationWiseAdminAccess,
  isTrialAllowed,
  isSelfSubscribeAllowed,
  locationWiseSubscriptionEligibilityMap,
  activeStates,
  locationWiseCFMap,
}: {
  selectedLocationIds: string[];
  locationWiseAdminAccess: Record<string, boolean>;
  isTrialAllowed: boolean;
  isSelfSubscribeAllowed: boolean;
  locationWiseSubscriptionEligibilityMap: Record<
    string,
    FreeTrialSubscriptionTypes.IsMultiEligibleForSelfUpgradeOrTrialDetail
  >;
  locationWiseCFMap: Record<string, LocationFeatureResponse | undefined>;
  activeStates: State[];
}) => {
  return selectedLocationIds.map<LocationSubscriptionData>((locationId) => {
    // Data from from location customization flags is given priority over subscription eligibility data
    // to determine if the state is active or not
    // This is because the subscription eligibility data may not be up to date
    const isStateActive = locationWiseCFMap[locationId]?.state
      ? activeStates.includes(locationWiseCFMap[locationId].state)
      : locationWiseSubscriptionEligibilityMap[locationId]?.isStateActive;
    const subscriptionStatus =
      locationWiseSubscriptionEligibilityMap[locationId]?.subscriptionStatus ??
      FreeTrialSubscriptionTypes.SubscriptionStatusForSelfUpgrade.SUBSCRIPTION_STATUS_UNSPECIFIED;
    const trialStatus =
      locationWiseSubscriptionEligibilityMap[locationId]?.trialStatus ??
      FreeTrialSubscriptionTypes.TrialStatus.TRIAL_STATUS_UNSPECIFIED;

    return {
      canManageAddons: locationWiseAdminAccess[locationId],
      subscriptionStatus:
        // Convert SUBSCRIBE status to SCHEDULE_CALL if it's not allowed to self-subscribe
        !isSelfSubscribeAllowed &&
        subscriptionStatus === FreeTrialSubscriptionTypes.SubscriptionStatusForSelfUpgrade.SUBSCRIBE
          ? FreeTrialSubscriptionTypes.SubscriptionStatusForSelfUpgrade.SCHEDULE_CALL
          : subscriptionStatus,
      trialStatus:
        // Convert TRIAL_ELIGIBLE status to TRIAL_STATUS_UNSPECIFIED if it's not trials are not allowed
        !isTrialAllowed && trialStatus === FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ELIGIBLE
          ? FreeTrialSubscriptionTypes.TrialStatus.TRIAL_STATUS_UNSPECIFIED
          : trialStatus,
      locationId,
      isStateActive,
    };
  });
};

export const checkTrialEligibility = ({ isStateActive, trialStatus }: LocationSubscriptionData) => {
  return !isStateActive && trialStatus === FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ELIGIBLE;
};

export const checkUpgradeEligibility = ({
  isStateActive,
  trialStatus,
  subscriptionStatus,
}: LocationSubscriptionData) => {
  return (
    !(isStateActive && trialStatus !== FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ACTIVE) &&
    subscriptionStatus !== FreeTrialSubscriptionTypes.SubscriptionStatusForSelfUpgrade.SUBSCRIPTION_STATUS_UNSPECIFIED
  );
};

export const checkSelfSubscribeEligibility = (subscriptionData: LocationSubscriptionData) => {
  return subscriptionData.subscriptionStatus === FreeTrialSubscriptionTypes.SubscriptionStatusForSelfUpgrade.SUBSCRIBE;
};

export const checkCanStartTrial = (subscriptionData: LocationSubscriptionData) => {
  return subscriptionData.canManageAddons && checkTrialEligibility(subscriptionData);
};

export const checkCanSelfSubscribe = (subscriptionData: LocationSubscriptionWithBillingData) => {
  return (
    subscriptionData.canManageAddons &&
    !subscriptionData.hasOverdueBalance &&
    checkUpgradeEligibility(subscriptionData) &&
    checkSelfSubscribeEligibility(subscriptionData)
  );
};

export const checkSubscriptionActive = ({ isStateActive, trialStatus }: LocationSubscriptionData) => {
  return isStateActive || trialStatus === FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ACTIVE;
};

export const checkSubscriptionActiveWithoutTrial = ({ isStateActive, trialStatus }: LocationSubscriptionData) => {
  return isStateActive && trialStatus !== FreeTrialSubscriptionTypes.TrialStatus.TRIAL_ACTIVE;
};

export const getFeatureFreeTrialDays = (feature: Feature): number => {
  switch (feature) {
    case Feature.CALL_INTELLIGENCE:
      return 14;
    default:
      return 30;
  }
};

export const getUpgradeBundleDisplayName = (bundle: SalesforceAccountProductBundle): string => {
  switch (bundle) {
    case SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_ULTIMATE:
    case SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_ULTIMATE:
      return i18next.t('Ultimate', { ns: 'bundleUpgrade' });
    default:
      return '';
  }
};

export const getBundleTrialDays = (bundle: SalesforceAccountProductBundle): number => {
  switch (bundle) {
    case SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_CORE_ULTIMATE:
    case SalesforceAccountProductBundle.PRODUCT_BUNDLE_WEAVE_PLUS_ULTIMATE:
      return 30;
    default:
      return 0;
  }
};

export const getScheduleDemoLink = (campaign: Feature | 'bundle', email = '') => {
  const demoLink = 'https://www.getweave.com/campaign/upgrade/';
  const searchParams = new URLSearchParams({
    title: 'Schedule a demo to explore your options', // This is for url no need to translate
    utm_campaign: campaign,
    email,
    utm_source: 'product',
  });

  return `${demoLink}?${searchParams.toString()}`;
};

const FeatureChillipiperOnboardingTypeMap: Partial<Record<Feature, string>> = {
  [Feature.FORMS]: '64ff89461c5178020e8bb181',
  [Feature.TEXT_CONNECT]: '5a9f51199cf5260001f8ca67',
  [Feature.ONLINE_SCHEDULING]: '64ff8c1d1c5178020e8bb297',
  [Feature.PRACTICE_ANALYTICS]: '652829d5d593786d70b20303',
};

export const getOnboardingCallLink = (feature: Feature) => {
  const baseUrl = 'https://getweave.chilipiper.com/book/upsell';
  const searchParams = new URLSearchParams({
    type: FeatureChillipiperOnboardingTypeMap[feature] ?? '',
    back: 'true',
  });

  return `${baseUrl}?${searchParams.toString()}`;
};

export const getBillingPriceText = ({
  price,
  subscriptionTerm = 'monthly',
  isMulti = false,
}: {
  price: number;
  subscriptionTerm?: FreeTrialSubscriptionTypes.BillingCycle;
  isMulti?: boolean;
}) => {
  const subscriptionTermText =
    subscriptionTerm === 'annually'
      ? i18next.t('/year', { ns: 'featurePromotion' })
      : i18next.t('/mo', { ns: 'featurePromotion' });

  const priceText = `$${Number(price).toLocaleString('en-US')}${subscriptionTermText}`;
  return isMulti ? `${priceText} ${i18next.t('per location', { ns: 'subscription' })}` : priceText;
};
